/**
 * Thread-scoped right-panel surface state.
 *
 * Ported from t3code's `rightPanelStore`. This is a shallow workspace model: it
 * owns an ordered set of surface descriptors and which one is active, while each
 * feature keeps owning its own durable resource state. Browser surfaces point at
 * preview tab ids, file surfaces point at workspace paths, and the rest are
 * singletons.
 *
 * This replaces the previous URL-as-state model, which could only express one
 * instance per kind — `RightSidebarShell.logic.ts` documented that as a ceiling
 * holding "until the underlying URL state supports multiple concurrent
 * right-pane tabs". Two files open at once is the whole point of a file panel,
 * so the ceiling is lifted here rather than worked around again.
 *
 * The `?rightPanel=` search contract is now **input only**: it is read once when
 * a thread mounts, to seed this store, and never written back. That is the same
 * treatment `diffRouteSearch` already gave the pre-consolidation boolean flags —
 * shared links keep working, and there is exactly one live representation of the
 * state, so nothing can drift out of step with anything else.
 */
import type { TurnId } from "@cmmd-forge/contracts";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { browserStorage } from "./lib/storage";
import {
  EMPTY_THREAD_STATE,
  fileSurface,
  pullRequestSurface,
  singletonSurface,
  browserSurface,
  type PullRequestSurfaceInput,
  type RightPanelSurface,
  type SingletonRightPanelKind,
  type ThreadRightPanelState,
} from "./rightPanelSurface";
import { migratePersistedRightPanelState } from "./rightPanelStorePersistence";

const RIGHT_PANEL_STORAGE_KEY = "forge:right-panel-state";
const RIGHT_PANEL_STORAGE_VERSION = 3;

/** The workspace inbox panel is session-only, matching the upstream PR list contract. */
const isPullRequestsPanelKey = (key: string): boolean => key === "workspace:pull-requests";

/**
 * Draft threads have no thread id, so they are keyed separately and never
 * persisted: a draft does not survive a restart, so neither should its panel
 * layout — restoring tabs onto a thread that no longer exists is worse than
 * opening clean.
 */
export function draftRightPanelKey(draftId: string): string {
  return `draft:${draftId}`;
}

function isDraftKey(key: string): boolean {
  return key.startsWith("draft:");
}

/**
 * Coerces a reveal line to a positive integer, or null when it is absent or
 * not finite.
 */
function normalizeRevealLine(line: number | undefined | null): number | null {
  if (line === undefined || line === null || !Number.isFinite(line)) return null;
  return Math.max(1, Math.trunc(line));
}

const upsertSurface = (
  current: ThreadRightPanelState,
  surface: RightPanelSurface,
  activate = true,
): ThreadRightPanelState => ({
  // Spread first: these builders used to construct a fresh object and silently
  // drop any layout state not listed, so opening a tab un-maximized the panel.
  ...current,
  isOpen: true,
  surfaces: current.surfaces.some((entry) => entry.id === surface.id)
    ? current.surfaces
    : [...current.surfaces, surface],
  activeSurfaceId: activate ? surface.id : current.activeSurfaceId,
});

/**
 * Applies a rename. A blank name clears the override rather than storing one
 * nothing displays: the tab falls back to its derived label. The key is
 * dropped rather than nulled, so a surface never carries a `label` that is
 * only undefined.
 */
function renameSurfaceLabel(surface: RightPanelSurface, label: string): RightPanelSurface {
  if (label.length === 0) {
    const { label: _cleared, ...withoutLabel } = surface;
    return withoutLabel;
  }
  return { ...surface, label };
}

/**
 * Applies an update and drops the thread's entry entirely once it holds nothing.
 * Without this, every thread ever visited keeps an empty record forever and the
 * persisted blob grows without bound.
 */
const updateThread = (
  byThreadKey: Record<string, ThreadRightPanelState>,
  threadKey: string,
  updater: (current: ThreadRightPanelState) => ThreadRightPanelState,
): Record<string, ThreadRightPanelState> => {
  const current = byThreadKey[threadKey] ?? EMPTY_THREAD_STATE;
  const next = updater(current);
  if (!next.isOpen && next.activeSurfaceId === null && next.surfaces.length === 0) {
    if (!(threadKey in byThreadKey)) return byThreadKey;
    const { [threadKey]: _removed, ...rest } = byThreadKey;
    return rest;
  }
  if (next === current) return byThreadKey;
  return { ...byThreadKey, [threadKey]: next };
};

interface RightPanelStoreState {
  readonly byThreadKey: Record<string, ThreadRightPanelState>;
  readonly open: (key: string, kind: SingletonRightPanelKind) => void;
  readonly openBrowser: (key: string, tabId: string | null) => void;
  /** Show the browser: activate the session already open, or start a placeholder. */
  readonly revealBrowser: (key: string) => void;
  readonly openFile: (
    key: string,
    relativePath: string,
    line?: number | null,
    readPolicy?: import("./rightPanelSurface").FileReadPolicy,
  ) => void;
  readonly openDiff: (key: string, turnId?: TurnId | null, filePath?: string | null) => void;
  readonly openPullRequest: (key: string, input: PullRequestSurfaceInput) => void;
  readonly activateSurface: (key: string, surfaceId: string) => void;
  readonly closeSurface: (key: string, surfaceId: string) => void;
  readonly closeOtherSurfaces: (key: string, surfaceId: string) => void;
  readonly closeSurfacesToRight: (key: string, surfaceId: string) => void;
  readonly closeAllSurfaces: (key: string) => void;
  /** Name a tab, or clear the name with a blank string. See `RightPanelSurface["label"]`. */
  readonly renameSurface: (key: string, surfaceId: string, label: string) => void;
  readonly show: (key: string) => void;
  readonly close: (key: string) => void;
  readonly toggleVisibility: (key: string) => void;
  /** Give the panel the whole window, or hand the transcript its half back. */
  readonly toggleMaximized: (key: string) => void;
  readonly removeThread: (key: string) => void;
}

export const useRightPanelStore = create<RightPanelStoreState>()(
  persist(
    (set) => ({
      byThreadKey: {},

      open: (key, kind) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) => {
            const existing = current.surfaces.find((surface) => surface.kind === kind);
            return upsertSurface(current, existing ?? singletonSurface(kind));
          }),
        })),

      openBrowser: (key, tabId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) => {
            // Navigating the placeholder replaces it rather than leaving an
            // orphaned "new tab" beside the session it became.
            const withoutPlaceholder = tabId
              ? current.surfaces.filter((entry) => entry.id !== "browser:new")
              : current.surfaces;
            return upsertSurface(
              { ...current, surfaces: withoutPlaceholder },
              browserSurface(tabId),
            );
          }),
        })),

      revealBrowser: (key) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) => {
            // "Show me the browser", not "give me another tab" — opening a
            // placeholder beside a live session is the bug this avoids.
            const existing = current.surfaces.find((surface) => surface.kind === "preview");
            return upsertSurface(current, existing ?? browserSurface(null));
          }),
        })),

      openDiff: (key, turnId, filePath) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) => {
            const surface: RightPanelSurface = {
              id: "diff",
              kind: "diff",
              turnId: turnId ?? null,
              // A file selection without a turn addresses nothing.
              filePath: turnId ? (filePath ?? null) : null,
            };
            return {
              ...current,
              isOpen: true,
              activeSurfaceId: surface.id,
              surfaces: current.surfaces.some((entry) => entry.id === "diff")
                ? current.surfaces.map((entry) => (entry.id === "diff" ? surface : entry))
                : [...current.surfaces, surface],
            };
          }),
        })),

      openFile: (key, relativePath, line, readPolicy = "execution-intent") =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) => {
            const surfaceId = `file:${relativePath}` as const;
            const existing = current.surfaces.find(
              (surface): surface is Extract<RightPanelSurface, { kind: "file" }> =>
                surface.id === surfaceId && surface.kind === "file",
            );
            // Bumped every time, including when the line is unchanged: asking
            // for the same line twice is a real request to scroll back to it,
            // and equal state would make the viewer ignore the second one.
            const surface = fileSurface(
              relativePath,
              normalizeRevealLine(line),
              (existing?.revealRequestId ?? 0) + 1,
              readPolicy,
            );
            return {
              ...current,
              isOpen: true,
              activeSurfaceId: surface.id,
              surfaces: existing
                ? current.surfaces.map((entry) => (entry.id === surface.id ? surface : entry))
                : [...current.surfaces, surface],
            };
          }),
        })),

      openPullRequest: (key, input) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) =>
            upsertSurface(current, pullRequestSurface(input)),
          ),
        })),

      activateSurface: (key, surfaceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) =>
            current.surfaces.some((surface) => surface.id === surfaceId)
              ? { ...current, isOpen: true, activeSurfaceId: surfaceId }
              : current,
          ),
        })),

      closeSurface: (key, surfaceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) => {
            const index = current.surfaces.findIndex((surface) => surface.id === surfaceId);
            if (index < 0) return current;
            const surfaces = current.surfaces.filter((surface) => surface.id !== surfaceId);
            if (current.activeSurfaceId !== surfaceId) {
              return { ...current, isOpen: surfaces.length > 0 && current.isOpen, surfaces };
            }
            // Activate the tab that slid into the closed one's position, or the
            // new last one — the same thing an editor does.
            const fallback = surfaces[Math.min(index, surfaces.length - 1)] ?? null;
            return {
              ...current,
              isOpen: surfaces.length > 0 && current.isOpen,
              surfaces,
              activeSurfaceId: fallback?.id ?? null,
            };
          }),
        })),

      closeOtherSurfaces: (key, surfaceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) => {
            const surface = current.surfaces.find((entry) => entry.id === surfaceId);
            if (!surface || current.surfaces.length === 1) return current;
            return { ...current, isOpen: true, surfaces: [surface], activeSurfaceId: surface.id };
          }),
        })),

      closeSurfacesToRight: (key, surfaceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) => {
            const index = current.surfaces.findIndex((surface) => surface.id === surfaceId);
            if (index < 0 || index === current.surfaces.length - 1) return current;
            const surfaces = current.surfaces.slice(0, index + 1);
            const activeStillExists = surfaces.some(
              (surface) => surface.id === current.activeSurfaceId,
            );
            return {
              ...current,
              surfaces,
              activeSurfaceId: activeStillExists ? current.activeSurfaceId : surfaceId,
            };
          }),
        })),

      closeAllSurfaces: (key) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, () => EMPTY_THREAD_STATE),
        })),

      renameSurface: (key, surfaceId, label) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) => {
            const trimmed = label.trim();
            return {
              ...current,
              surfaces: current.surfaces.map((surface) =>
                surface.id === surfaceId ? renameSurfaceLabel(surface, trimmed) : surface,
              ),
            };
          }),
        })),

      show: (key) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) => ({
            ...current,
            isOpen: true,
          })),
        })),

      // Hiding keeps the surfaces and the selection: reopening the panel should
      // return to what you were looking at, not an empty shell.
      close: (key) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) =>
            current.isOpen ? { ...current, isOpen: false, isMaximized: false } : current,
          ),
        })),

      toggleVisibility: (key) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) => ({
            ...current,
            isOpen: !current.isOpen,
          })),
        })),

      toggleMaximized: (key) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, key, (current) =>
            // Gated on the panel being open, not on it having surfaces: an
            // open panel showing its empty launcher is still taking half the
            // window, and the control is offered in exactly that state. Gating
            // on surfaces made it render and do nothing.
            current.isOpen ? { ...current, isMaximized: !(current.isMaximized ?? false) } : current,
          ),
        })),

      removeThread: (key) =>
        set((state) => {
          if (!(key in state.byThreadKey)) return state;
          const { [key]: _removed, ...rest } = state.byThreadKey;
          return { byThreadKey: rest };
        }),
    }),
    {
      name: RIGHT_PANEL_STORAGE_KEY,
      version: RIGHT_PANEL_STORAGE_VERSION,
      storage: createJSONStorage(() => browserStorage),
      // Drafts and the workspace PR inbox are session-only. Restoring either
      // would attach tabs to a context that intentionally starts fresh.
      partialize: (state) => ({
        byThreadKey: Object.fromEntries(
          Object.entries(state.byThreadKey).filter(
            ([key]) => !isDraftKey(key) && !isPullRequestsPanelKey(key),
          ),
        ),
      }),
      migrate: (persisted) => migratePersistedRightPanelState(persisted),
    },
  ),
);
